import { useIntl } from 'react-intl';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';

import { useGlobalStore } from '@/store/global';
import { getUser } from '@/store/global/user/selectors';
import { getUserRoles } from '@/store/global/userRoles/selectors';
import { CheckboxGroup } from '../CheckboxGroup';

import type { CheckboxItem } from '../CheckboxGroup';

type FilterEntry = { id: string; value: string[] };

type Props = {
  onFilter?: (filters: FilterEntry[]) => void;
  value?: string[];
};

export default function Creatives({ onFilter, value = [] }: Props) {
  const { formatMessage } = useIntl();
  const user = useGlobalStore(getUser);
  const userRoles = useGlobalStore(getUserRoles);

  const allOptions: CheckboxItem[] = [
    {
      id: 'display',
      label: formatMessage({
        defaultMessage: 'Display',
        id: 'campaigns.lineItems.advancedFilters.creatives.display'
      })
    },
    {
      id: 'video',
      label: formatMessage({
        defaultMessage: 'Video',
        id: 'campaigns.lineItems.advancedFilters.creatives.video'
      }),
      show: checkUserResourcePermissions('adFormatVideo', user.toJSON(), userRoles)
    },
    {
      id: 'thirdParty',
      label: formatMessage({
        defaultMessage: 'Third Party',
        id: 'campaigns.lineItems.advancedFilters.creatives.thirdParty'
      }),
      show: checkUserResourcePermissions('thirdPartyCreative', user.toJSON(), userRoles),
      tooltipText: formatMessage({
        defaultMessage:
          'Refers to using external ad server tags (wrappers) within the creative. This allows for independent tracking, rich media features, and easier management across multiple platforms.',
        id: 'campaigns.lineItems.advancedFilters.creatives.thirdParty.tooltip'
      })
    },
    {
      id: 'vast',
      label: formatMessage({
        defaultMessage: 'Vast',
        id: 'campaigns.lineItems.advancedFilters.creatives.vast'
      }),
      show:
        ['adFormatCtvVast', 'adFormatRewardedVast', 'adFormatVast', 'adFormatVastOutStream'].filter(permission =>
          checkUserResourcePermissions(permission, user.toJSON(), userRoles)
        ).length > 0,
      tooltipText: formatMessage({
        defaultMessage:
          'A universal XML schema that allows video players and ad servers to communicate, ensuring video ads play correctly across different platforms and devices.',
        id: 'campaigns.lineItems.advancedFilters.creatives.vast.tooltip'
      })
    },
    {
      id: 'daast',
      label: formatMessage({
        defaultMessage: 'Daast',
        id: 'campaigns.lineItems.advancedFilters.creatives.daast'
      }),
      show: checkUserResourcePermissions('adFormatDaast', user.toJSON(), userRoles),
      tooltipText: formatMessage({
        defaultMessage:
          'The industry standard for delivering audio ads. It provides a consistent structure for ads in podcasts and digital radio streams.',
        id: 'campaigns.lineItems.advancedFilters.creatives.daast.tooltip'
      })
    }
  ];

  const handleChange = (_id: string, selected: string[]) => {
    const filter: FilterEntry[] = [{ id: 'creatives', value: selected }];
    onFilter?.(filter);
  };

  return (
    <CheckboxGroup
      id="creatives"
      onChange={handleChange}
      options={allOptions}
      selected={value}
      title={formatMessage({
        defaultMessage: 'Creatives',
        id: 'campaigns.lineItems.advancedFilters.creatives.title'
      })}
    />
  );
}
